<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="8">
            <h1 class="h4 mb-1 font-weight-bold">General ledger</h1>
            <p class="text-muted mb-0">
              Debits and credits posted to each account
            </p>
          </CCol>
          <CCol md="4" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <!-- Filters -->
        <CRow class="mb-2">
          <CCol sm="6" lg="3">
            <CInput
              type="date"
              label="From"
              :value="filter.startDate"
              @change="(v) => onDateChange('startDate', v)"
            />
          </CCol>
          <CCol sm="6" lg="3">
            <CInput
              type="date"
              label="To"
              :value="filter.endDate"
              :is-valid="dateRangeValid ? null : false"
              invalid-feedback="Choose a date on or after the From date."
              @change="(v) => onDateChange('endDate', v)"
            />
          </CCol>
          <CCol sm="12" lg="4">
            <div class="form-group">
              <label for="gl-account">Account</label>
              <select
                id="gl-account"
                class="form-control"
                v-model="filter.chartAccountId"
                @change="refreshTable"
              >
                <option :value="null">All accounts</option>
                <option
                  v-for="acc in chartOfAccountItems"
                  :key="acc.id"
                  :value="acc.id"
                >
                  {{ acc.itemDisplay }}
                </option>
              </select>
            </div>
          </CCol>
          <CCol lg="2" class="d-flex align-items-end">
            <CButton
              v-if="hasFilters"
              color="light"
              class="mb-3"
              @click="resetFilter"
            >
              <CIcon name="cil-x" /> Clear filters
            </CButton>
          </CCol>
        </CRow>

        <div v-if="loading && !items.length" class="text-center p-5">
          <CSpinner color="primary" />
          <div class="text-muted mt-2">Loading the general ledger…</div>
        </div>

        <div v-else-if="error && !items.length" class="text-center p-5">
          <CIcon name="cil-warning" size="xl" class="text-danger mb-2" />
          <h2 class="h6 font-weight-bold">Couldn't load the general ledger</h2>
          <p class="text-muted">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Retry
          </CButton>
        </div>

        <div v-else-if="!items.length && !hasFilters" class="text-center p-5">
          <CIcon name="cil-book" size="xl" class="text-muted mb-2" />
          <h2 class="h6 font-weight-bold">Nothing posted yet</h2>
          <p class="text-muted mb-0">
            Approved invoices, bills, payments and journal entries appear here,
            grouped by account.
          </p>
        </div>

        <div v-else-if="!items.length" class="text-center p-4">
          <h2 class="h6 font-weight-bold">Nothing matches your filters</h2>
          <CButton color="light" @click="resetFilter">
            <CIcon name="cil-x" /> Clear filters
          </CButton>
        </div>

        <template v-else>
          <!-- Desktop table -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="items"
              :fields="fields"
              items-per-page-select
              :items-per-page="25"
              hover
              sorter
              pagination
              :loading="loading"
            >
              <template #totalDebit="{ item }">
                <td class="text-right">RM {{ formatMyr(item.totalDebit) }}</td>
              </template>
              <template #totalCredit="{ item }">
                <td class="text-right">RM {{ formatMyr(item.totalCredit) }}</td>
              </template>
              <template #show_details="{ item, index }">
                <td class="py-2">
                  <CButton
                    color="light"
                    size="sm"
                    :aria-label="item._toggled ? `Hide entries for ${item.accountName}` : `Show entries for ${item.accountName}`"
                    :aria-expanded="Boolean(item._toggled) ? 'true' : 'false'"
                    @click="toggleDetails(item, index)"
                  >
                    <CIcon :name="item._toggled ? 'cil-chevron-top' : 'cil-chevron-bottom'" />
                  </CButton>
                </td>
              </template>
              <template #details="{ item }">
                <CCollapse :show="Boolean(item._toggled)">
                  <CCardBody>
                    <CDataTable
                      :items="item.items"
                      :fields="journalFields"
                      small
                      bordered
                    >
                      <template #date="{ item: line }">
                        <td>{{ formatDate(line.date) }}</td>
                      </template>
                      <template #journalNo="{ item: line }">
                        <td class="text-monospace">{{ line.journalNo }}</td>
                      </template>
                      <template #debit="{ item: line }">
                        <td class="text-right">RM {{ formatMyr(line.debit) }}</td>
                      </template>
                      <template #credit="{ item: line }">
                        <td class="text-right">RM {{ formatMyr(line.credit) }}</td>
                      </template>
                    </CDataTable>
                  </CCardBody>
                </CCollapse>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile card list -->
          <div class="d-lg-none">
            <div
              v-for="(item, index) in items"
              :key="item.accountName + index"
              class="border rounded p-3 mb-2"
            >
              <div class="font-weight-bold mb-1">{{ item.accountName }}</div>
              <div class="d-flex justify-content-between small">
                <span class="text-muted">Debit</span>
                <span>RM {{ formatMyr(item.totalDebit) }}</span>
              </div>
              <div class="d-flex justify-content-between small">
                <span class="text-muted">Credit</span>
                <span>RM {{ formatMyr(item.totalCredit) }}</span>
              </div>
              <CButton
                color="link"
                class="px-0"
                :aria-expanded="Boolean(item._toggled) ? 'true' : 'false'"
                @click="toggleDetails(item, index)"
              >
                {{ item._toggled ? "Hide entries" : `Show entries (${(item.items || []).length})` }}
              </CButton>
              <div v-if="item._toggled">
                <div
                  v-for="(line, i) in item.items || []"
                  :key="i"
                  class="border-top pt-2 mt-2 small"
                >
                  <div class="d-flex justify-content-between">
                    <span class="text-monospace">{{ line.journalNo }}</span>
                    <span class="text-muted">{{ formatDate(line.date) }}</span>
                  </div>
                  <div v-if="line.description">{{ line.description }}</div>
                  <div class="d-flex justify-content-between">
                    <span>Dr RM {{ formatMyr(line.debit) }}</span>
                    <span>Cr RM {{ formatMyr(line.credit) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import ReportApi from "@/lib/reportApi";
import ChartOfAccountApi from "@/lib/chartOfAccountApi";
import { formatMyr } from "@/utils/money";
import { formatDate, parseDateInput } from "@/utils/datetime";

export default {
  name: "GeneralLedger",
  data() {
    return {
      loading: true,
      error: false,
      chartOfAccountItems: [],
      fields: [
        { key: "accountName", label: "Account" },
        {
          key: "totalDebit",
          label: "Total debit",
          _classes: "text-right",
          _style: "width:160px",
        },
        {
          key: "totalCredit",
          label: "Total credit",
          _classes: "text-right",
          _style: "width:160px",
        },
        {
          key: "show_details",
          label: "",
          _style: "width:1%",
          sorter: false,
        },
      ],
      journalFields: [
        { key: "date", label: "Date" },
        { key: "journalNo", label: "Journal no." },
        { key: "description", label: "Description" },
        { key: "debit", label: "Debit", _classes: "text-right" },
        { key: "credit", label: "Credit", _classes: "text-right" },
      ],
      filter: {
        startDate: null,
        endDate: null,
        chartAccountId: null,
      },
      items: [],
      api: new ReportApi(),
      chartOfAccountApi: new ChartOfAccountApi(),
    };
  },
  computed: {
    hasFilters() {
      return Boolean(
        this.filter.startDate || this.filter.endDate || this.filter.chartAccountId
      );
    },
    dateRangeValid() {
      const { startDate, endDate } = this.filter;
      return !startDate || !endDate || endDate >= startDate;
    },
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Accounting", "General ledger"]);
    this.fetchChartOfAccounts();
    this.refreshTable();
  },
  methods: {
    formatMyr,
    formatDate,
    fetchChartOfAccounts() {
      this.chartOfAccountApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.chartOfAccountItems = (response.result || []).map((acc) => ({
            ...acc,
            itemDisplay: [acc.accountNo, acc.name].filter(Boolean).join(" - "),
          }));
        })
        .catch((err) => {
          // The account filter just stays at "All accounts".
          console.error(err);
        });
    },
    onDateChange(key, value) {
      this.filter[key] = parseDateInput(value) || null;
      if (this.dateRangeValid) this.refreshTable();
    },
    resetFilter() {
      this.filter.startDate = null;
      this.filter.endDate = null;
      this.filter.chartAccountId = null;
      this.refreshTable();
    },
    toggleDetails(item, index) {
      this.$set(this.items[index], "_toggled", !item._toggled);
    },
    refreshTable() {
      this.loading = true;
      this.error = false;

      const params = {
        startDate: this.filter.startDate,
        endDate: this.filter.endDate,
        chartAccountId: this.filter.chartAccountId,
      };
      this.api
        .getGeneralLedger(params)
        .then((response) => {
          this.items = response.result || [];
        })
        .catch((err) => {
          console.error(err);
          this.error = true;
          if (this.items.length) {
            this.$toast.error(
              "Couldn't refresh the general ledger. Check your connection and try again."
            );
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
